/**
 * Client features — which agent pages a CLIENT (no `show_cowork`) may open.
 *
 * Staff see everything. A client sees nothing of the agent layer until Beatly opens a feature for
 * their team (backend engine/clientFeatures.ts, flipped from the staff client switcher). GET /v1/me
 * returns the team's list; this maps each feature to the pages it unlocks. Everything else stays
 * behind the show_cowork gate exactly as before.
 *
 * `autopilot-client` (28 Oct 2026) is the one switch for the new client experience: Lykke (home bar, Needs you, her dock
 * and page, the new layout), the client kickoff with its order confirmation, the new campaign page (stages sidebar,
 * Overview, Brief document with review and send bar, timeline), the scouting view with criteria and Why this pick, and
 * the Creators status tabs. Beatly staff always see it; a client team only once staff open it; never touched, or /v1/me
 * failed, = the Omni clients had before (see newClientExperienceOn).
 */
import type { CurrentPage } from '@/routes';

export const CLIENT_FEATURE_PAGES: Record<string, CurrentPage[]> = {
  reports: ['agent-reports'],
  dashboards: ['agent-dashboards'],
  brand: ['agent-brand', 'agent-brand-pulse', 'agent-brand-id', 'agent-brand-promotables', 'agent-brand-audiences', 'agent-brand-library'],
  // Campaign creation lives on /campaigns/new (a platform page); the feature switches it on there, see App.tsx.
  campaigns: [],
  // The new client experience is drawn by App.tsx and the campaign page, not by unlocking agent pages.
  'autopilot-client': [],
  // Audio Studio (enforced by the audio service itself) and Video Studio; either one opens the Ad Studio landing.
  audio: ['audio-studio', 'ad-studios'],
  video: ['agent-video-studio', 'ad-studios'],
};

/** The client feature that switches on the new client experience (cowork engine/clientFeatures.ts). */
export const NEW_CLIENT_EXPERIENCE = 'autopilot-client';

export const CLIENT_FEATURE_LABELS: Record<string, string> = {
  reports: 'Deck studio',
  dashboards: 'Dashboards',
  brand: 'Brand Intelligence',
  campaigns: 'Campaign creation',
  'autopilot-client': 'New client experience',
  audio: 'Audio Studio',
  video: 'Video Studio',
};

/**
 * The studios a client team can be switched into (launch, Nov 2026), and the pages each one draws. A client who opens
 * one of these pages without the feature gets the calm "not switched on" page instead of a redirect or an error.
 */
const STUDIO_FEATURES_BY_PAGE: Partial<Record<CurrentPage, readonly string[]>> = {
  'agent-reports': ['reports'],
  'agent-dashboards': ['dashboards'],
  'agent-brand': ['brand'],
  'agent-brand-pulse': ['brand'],
  'agent-brand-id': ['brand'],
  'agent-brand-promotables': ['brand'],
  'agent-brand-audiences': ['brand'],
  'agent-brand-library': ['brand'],
  'audio-studio': ['audio'],
  'agent-video-studio': ['video'],
  'ad-studios': ['audio', 'video'],
};

/** The client features that are studios (the AI terms are asked of a client team with at least one of them). */
export const STUDIO_FEATURE_IDS: readonly string[] = ['reports', 'dashboards', 'brand', 'audio', 'video'];

/** The client features that open this page as a studio (any one of them is enough); empty for every other page. */
export function studioFeaturesForPage(page: string): readonly string[] {
  return STUDIO_FEATURES_BY_PAGE[page as CurrentPage] ?? [];
}

export function isStudioPage(page: string): boolean {
  return studioFeaturesForPage(page).length > 0;
}

/** What the studio is called on the calm "not switched on" page. */
export function studioNameForPage(page: string): string {
  const f = studioFeaturesForPage(page);
  if (f.length > 1) return 'Ad Studio';
  if (f[0] === 'brand') return 'Brand OS';
  if (f[0] === 'reports') return 'Deck Studio';
  return f[0] ? CLIENT_FEATURE_LABELS[f[0]] ?? 'This studio' : 'This studio';
}

/**
 * A positive sign, from the api-v2 sign-in alone, that this is a Beatly staff session: a BO admin session (user_id
 * `admin-…`) or a beatly.com account. Only used while GET /v1/me has never answered: `show_cowork` alone is not enough,
 * because pilot client accounts (Merrell) have it too. The API still refuses what a caller may not do.
 */
export function beatlyStaffHint(user: { user_id?: string | null; email?: string | null } | null | undefined): boolean {
  if (!user) return false;
  if (typeof user.user_id === 'string' && user.user_id.startsWith('admin-')) return true;
  return typeof user.email === 'string' && /@beatly\.com$/i.test(user.email.trim());
}

/**
 * Who is looking, as far as the studios are concerned:
 * - `staff`: Beatly staff per GET /v1/me: everything, never paused, never capped.
 * - `client`: /v1/me answered for a client team: exactly the features Beatly opened, whatever `show_cowork` says.
 *   An answer once given is kept through a failed refetch (TanStack keeps the data beside the error).
 * - `legacy`: /v1/me never answered (it failed): only a `show_cowork` account with a positive staff sign
 *   (beatlyStaffHint) keeps the full app, so staff never lose it to a blip; anyone else gets a "try again" page on the
 *   studios and nothing of the agent layer until /v1/me answers.
 * - `loading`: /v1/me has not answered yet.
 */
export type StudioViewer =
  | { kind: 'loading' }
  | { kind: 'staff' }
  | { kind: 'legacy'; showCowork: boolean; staffHint?: boolean }
  | { kind: 'client'; features: readonly string[]; paused: boolean };

export function studioViewerFrom(
  me: { staff?: boolean | null; features?: readonly string[] | null; clientsPaused?: boolean | null } | null | undefined,
  state: { pending: boolean; failed: boolean; showCowork: boolean; staffHint?: boolean },
): StudioViewer {
  if (me && me.staff === true) return { kind: 'staff' };
  if (me) return { kind: 'client', features: Array.isArray(me.features) ? me.features : [], paused: me.clientsPaused === true };
  if (state.failed) return { kind: 'legacy', showCowork: state.showCowork, staffHint: state.staffHint === true };
  return { kind: 'loading' };
}

/**
 * Does this viewer see every cowork page (the old `show_cowork` behaviour)? Staff, or, when /v1/me never answered, a
 * show_cowork account that is also positively staff (beatlyStaffHint).
 */
export function hasFullAccess(v: StudioViewer): boolean {
  return v.kind === 'staff' || (v.kind === 'legacy' && v.showCowork && v.staffHint === true);
}

/** `unreachable`: /v1/me never answered for someone who is not positively staff; the page offers to try again. */
export type StudioPageState = 'open' | 'loading' | 'closed' | 'paused' | 'unreachable' | 'not-studio';

/** What a studio page draws for this viewer: the studio, a spinner, the calm closed / paused / try again page. */
export function studioPageState(page: string, v: StudioViewer): StudioPageState {
  const needs = studioFeaturesForPage(page);
  if (!needs.length) return 'not-studio';
  if (v.kind === 'loading') return 'loading';
  if (v.kind === 'staff') return 'open';
  if (v.kind === 'legacy') return hasFullAccess(v) ? 'open' : 'unreachable';
  if (v.paused) return 'paused';
  return needs.some((f) => v.features.includes(f)) ? 'open' : 'closed';
}

/**
 * Is this a page a paused client must not use (the pause switch stops every client's cowork work)? Every studio and
 * every page a client feature opens, Lykke's page, and ordering a campaign when it runs on the cowork API (staff or a
 * team's `campaigns` / new experience switch). Other pages (campaigns, content, the home) stay usable, with a paused
 * banner and without Lykke.
 */
export function pausedClientBlocks(page: string, opts: { clientPages: readonly string[]; orderingOn: boolean }): boolean {
  if (isStudioPage(page) || opts.clientPages.includes(page)) return true;
  if (page === 'lykke') return true;
  return page === 'campaigns' && opts.orderingOn;
}

/**
 * Does this session see the new client experience? Beatly staff always (in any team they view); a client only when
 * their team has `autopilot-client`. No answer yet, a failed /v1/me or a team never touched: no (the older Omni).
 */
export function newClientExperienceOn(me: { staff?: boolean | null; features?: readonly string[] | null } | null | undefined): boolean {
  if (!me) return false;
  if (me.staff === true) return true;
  return Array.isArray(me.features) && me.features.includes(NEW_CLIENT_EXPERIENCE);
}

/** Pages unlocked by a feature list; unknown ids are ignored. */
export function clientPagesFor(features: readonly string[] | undefined | null): CurrentPage[] {
  const out: CurrentPage[] = [];
  for (const f of features || []) for (const p of CLIENT_FEATURE_PAGES[f] || []) if (!out.includes(p)) out.push(p);
  return out;
}

export function isClientAllowedPage(page: string, clientPages: readonly string[]): boolean {
  return clientPages.includes(page);
}

export type ExperienceState = { on: boolean; known: boolean };

/** Which campaign page to draw: the new one, the older one (SeeCampaignsPageLegacy), or nothing yet (/v1/me loading). */
export function campaignPageFor(x: ExperienceState): 'new' | 'legacy' | 'loading' {
  if (!x.known) return 'loading';
  return x.on ? 'new' : 'legacy';
}

/**
 * Which order flow /campaigns/new draws once ordering is open (staff, `campaigns` or the new experience): the fast
 * planner with the new experience (an existing order, ?order=, or ?kickoff opens the client kickoff document), else the
 * v5 wizard clients had before; ?classic and ?v5 keep their flows reachable.
 */
export function orderFlowFor(x: ExperienceState, search: string): 'classic' | 'v5' | 'planner' | 'kickoff' | 'loading' {
  const q = new URLSearchParams(search);
  if (q.has('classic')) return 'classic';
  if (q.has('v5')) return 'v5';
  if (!x.known) return 'loading';
  if (!x.on) return 'v5';
  return q.has('order') || q.has('kickoff') ? 'kickoff' : 'planner';
}
